import {
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';

import {
    add,
    deleteRecord,
    fetch,
    update,
} from '../services/SubscriptionsService';
import {Subscription} from '../types';
import {subscriptionsKeys} from './useSubscriptionsQuery';
import {
    notifyError,
    notifySuccess,
} from '../../../components/ServiceNotifications';

/**
 * FNP-918: the mutation payload is WHITELISTED to the API's Input type (§6).
 *
 * It was built by `delete`-ing keys off a spread of the record
 * (`delete payload.created; delete payload.updated;`). Two problems, both
 * invisible in review:
 *
 *  1. It is a BLOCKLIST. Every field the form or the query happens to carry —
 *     `startDate`, `status`, `cancelAt`, `endedAt`, and the derived
 *     `createdAtMillis` / `startDateMillis` this ticket adds — was sent to an
 *     Input type that does not declare them. A whitelist cannot drift that way.
 *  2. Deriving the payload by mutation is one edit away from mutating the
 *     caller's live Formik/React Query object (FNP-996 shipped exactly that in
 *     `applications` for months).
 *
 * CreateSubscriptionInput / UpdateSubscriptionInput declare `customerId` and
 * `items`; the form collects exactly those two. `id` is added for update, which
 * addresses the record.
 */
const toInput = (values: Subscription) => ({
    customerId: values.customerId,
    items: values.items?.map(item => ({
        price: item.price,
        quantity: item.quantity,
    })),
});

export const useSubscription = (subscriptionId?: string) => {
    const queryClient = useQueryClient();

    const subscriptionQuery = useQuery({
        queryKey: subscriptionsKeys.detail(subscriptionId ?? ''),
        queryFn: () => fetch(subscriptionId!),
        enabled: !!subscriptionId,
    });

    const createMutation = useMutation({
        mutationFn: (values: Subscription) => add(toInput(values)),
        onSuccess: () => {
            notifySuccess('Subscription created');
            queryClient.invalidateQueries({queryKey: subscriptionsKeys.all});
        },
        onError: notifyError,
    });

    const updateMutation = useMutation({
        mutationFn: (values: Subscription) => {
            if (!subscriptionId) {
                throw new Error('subscriptionId is required parameter');
            }
            return update(subscriptionId, {
                id: subscriptionId,
                ...toInput(values),
            });
        },
        onSuccess: () => {
            notifySuccess('Subscription updated');
            // Detail AND list keys — the list row shows fields the edit changed
            // (§8). `subscriptionsKeys.all` covers both.
            queryClient.invalidateQueries({queryKey: subscriptionsKeys.all});
        },
        onError: notifyError,
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!subscriptionId) {
                throw new Error('subscriptionId is required parameter');
            }
            return deleteRecord(subscriptionId);
        },
        onSuccess: () => {
            notifySuccess('Subscription deleted');
            queryClient.invalidateQueries({queryKey: subscriptionsKeys.all});
        },
        onError: notifyError,
    });

    return {
        record: subscriptionQuery.data,
        loading: subscriptionQuery.isLoading,
        isError: subscriptionQuery.isError,
        fetchRecord: subscriptionQuery.refetch,
        // ⚠️ `mutateAsync`, not `mutate` (§6/§7). The drawer AWAITS this before
        // closing; `mutate` returns void, so the await resolved immediately and
        // the drawer closed before the mutation settled — a server-side
        // rejection then destroyed the operator's input and toasted into a
        // closed drawer.
        createRecord: createMutation.mutateAsync,
        updateRecord: updateMutation.mutateAsync,
        deleteRecord: deleteMutation.mutateAsync,
        createMutation,
        updateMutation,
        deleteMutation,
    };
};
